import bbox from '@turf/bbox';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import mapboxgl, { GeoJSONSource, LngLatBoundsLike } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import centroid from '@turf/centroid';
import expand_map from 'assets/zoom_out_map.svg';
import FitViewControl from './FitViewControl';
import ZoomControl from './ZoomControl';

export const defaultMapPadding = { left: 80, top: 80, bottom: 80, right: 80 };

type InstantStaticMapProps = {
  mapExpanded: any;
  expandMap: any;
  showExpand: any;
  item: any;
  isStatic: boolean;
  fieldType: any;
  width: string;
  height: string;
};

function InstantStaticMap({ mapExpanded, expandMap, item, width, height }: InstantStaticMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>();
  const [initialized, setInitialized] = useState<boolean>(false);

  const handleFitViewClick = useCallback(() => {
    if (!mapInstanceRef.current || !item.geometry) return;
    mapInstanceRef.current.fitBounds(bbox(item.geometry) as LngLatBoundsLike, {
      padding: defaultMapPadding,
    });
  }, [item]);

  const handleZoomInClick = useCallback(() => {
    mapInstanceRef.current?.zoomIn();
  }, []);

  const handleZoomOutClick = useCallback(() => {
    mapInstanceRef.current?.zoomOut();
  }, []);

  const canceled = useRef<boolean>(false);
  useEffect(() => {
    return () => {
      canceled.current = true;
    };
  });

  useEffect(() => {
    /**
     * Mapbox initial setup:
     */
    if (!mapRef.current || initialized) {
      return;
    }
    if (!initialized) {
      const _mapInstance = new mapboxgl.Map({
        container: mapRef.current,
        accessToken: import.meta.env.VITE_APP_MAP_KEY,
        style: 'mapbox://styles/mapbox/satellite-v9',
        center: { lat: 41.8787639554894, lng: -87.63450622558592 }, // center coordinates for the initial map rendering
        crossSourceCollisions: true,
        zoom: 14, // default initial zoom level
      });
      mapInstanceRef.current = _mapInstance;
    }
    const mapInstance = mapInstanceRef.current;
    mapInstance.on('load', () => {
      /**
       * Adding Data source that will contain the GeoJSON geometries for the fields
       */
      mapInstance.addSource('field-geometries', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        },
      });

      /**
       * Adding layer that will contain the fields
       */
      const DEFAULT_FILL_COLOR = '#cb894f';
      const DEFAULT_FILL_OPACITY = 0.9;
      const DEFAULT_OUTLINE_COLOR = '#4f95cb';
      mapInstance.addLayer({
        id: 'field-fills',
        type: 'fill',
        source: 'field-geometries', // <- This layer points to the field-geometries source declared above
        filter: ['==', ['geometry-type'], 'Polygon'],
        paint: {
          'fill-color': DEFAULT_FILL_COLOR,
          'fill-opacity': DEFAULT_FILL_OPACITY,
          'fill-outline-color': DEFAULT_OUTLINE_COLOR,
        },
      });
      mapInstance.addLayer({
        id: 'field-outlines',
        type: 'line',
        source: 'field-geometries', // <- This layer points to the field-geometries source declared above
        filter: ['==', ['geometry-type'], 'Polygon'],
        paint: {
          'line-color': DEFAULT_OUTLINE_COLOR,
          'line-width': 2,
        },
      });
      if (!canceled.current) {
        setInitialized(true);
      }
    });
  });

  useEffect(() => {
    if (!mapInstanceRef.current || !initialized || !item.geometry) return;
    const fieldsSource = mapInstanceRef.current.getSource('field-geometries') as GeoJSONSource;
    fieldsSource.setData(item.geometry);
    const _centroid = centroid(item.geometry);
    const center = _centroid.geometry.coordinates;
    mapInstanceRef.current.jumpTo({
      center: center,
      zoom: 13,
    });
  }, [item, width, height, mapExpanded, initialized]);

  return (
    <div className="relative" style={{ width, height }} data-testid="map_wrapper">
      {mapExpanded ? (
        <div className="absolute z-10 bottom-4 left-4 flex flex-col justify-end items-start pointer-events-auto">
          <FitViewControl callback={handleFitViewClick} />
          <ZoomControl zoomInCallback={handleZoomInClick} zoomOutCallback={handleZoomOutClick} />
        </div>
      ) : (
        <button
          className="absolute top-[5px] right-[5px] z-[1] rounded-[4px] bg-white"
          onClick={expandMap}
        >
          <img
            src={expand_map}
            alt="expand map"
            className="w-auto h-auto max-w-full max-h-full block"
          />
        </button>
      )}

      <div data-cy="map_wrapper" className="mapbox-container__map rounded-4" ref={mapRef}></div>
    </div>
  );
}
export default InstantStaticMap;
